import React, { useEffect, useState } from 'react';
import { apiCall } from '../utils/apiHelper';
import './Settings.css';

const SETTLEMENT_START_YEAR = 2026;
const SETTLEMENT_MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

function startMonthFromApi(value) {
  const match = String(value || '').match(/(?:^\d{4}-)?(\d{1,2})$/);
  if (!match) {
    return '';
  }
  const month = Number(match[1]);
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    return '';
  }
  return String(month);
}

function formatStartYm(month) {
  return `${SETTLEMENT_START_YEAR}-${String(month).padStart(2, '0')}`;
}

function formatStartLabel(apiValue) {
  const month = startMonthFromApi(apiValue);
  return month === '' ? '' : `${SETTLEMENT_START_YEAR}年${Number(month)}月〜`;
}

function formatAiDayLabel(isoDate) {
  const match = String(isoDate || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return isoDate || '';
  }
  return `${Number(match[2])}月${Number(match[3])}日`;
}

function formatAiMonthLabel(ym) {
  const match = String(ym || '').match(/^(\d{4})-(\d{2})$/);
  if (!match) {
    return ym || '';
  }
  return `${match[1]}年${Number(match[2])}月`;
}

function resolveAiMonthView(boxAiCalls, viewingPrevious) {
  const monthLimit = boxAiCalls?.monthLimit > 0 ? Number(boxAiCalls.monthLimit) : 1000;
  const currentMonth = boxAiCalls?.currentMonth || boxAiCalls?.month || '';
  const previousMonth = boxAiCalls?.previousMonth || '';
  const monthKey = viewingPrevious && previousMonth ? previousMonth : currentMonth;
  const monthBody = boxAiCalls?.months?.[monthKey];
  const monthTotal = Number.isFinite(Number(monthBody?.monthTotal))
    ? Number(monthBody.monthTotal)
    : (viewingPrevious ? 0 : Number(boxAiCalls?.monthTotal) || 0);
  const rawDays = Array.isArray(monthBody?.days)
    ? monthBody.days
    : (!viewingPrevious && Array.isArray(boxAiCalls?.days) ? boxAiCalls.days : []);
  // 0回の日は一覧に出さない
  const days = rawDays.filter((day) => (Number(day?.count) || 0) > 0);
  return {
    monthKey,
    monthLabel: formatAiMonthLabel(monthKey),
    monthLimit,
    monthTotal,
    days,
    canGoPrev: Boolean(previousMonth) && !viewingPrevious,
    canGoNext: Boolean(viewingPrevious),
  };
}

async function readJson(res) {
  const text = await res.text();
  if (!text) {
    throw new Error(`サーバーからの応答が空です (HTTP ${res.status})`);
  }
  try {
    return JSON.parse(text);
  } catch {
    if (text.startsWith('Proxy error') || text.includes('ECONNREFUSED')) {
      throw new Error('Javaサーバーに接続できません。http://localhost:8080 が起動しているか確認してください');
    }
    throw new Error(text.slice(0, 200));
  }
}

const TEAMS_WEBHOOK_CHANNELS = [
  {
    key: 'teamsWebhookUrl',
    hasKey: 'hasTeamsWebhookUrl',
    label: '一般',
    hint: 'GM承認・当月精算など',
  },
  {
    key: 'teamsWebhookUrlManagerApply',
    hasKey: 'hasTeamsWebhookUrlManagerApply',
    label: 'M申請',
    hint: '担当が「済」になったとき（マネージャーへの申請）',
  },
  {
    key: 'teamsWebhookUrlGmApply',
    hasKey: 'hasTeamsWebhookUrlGmApply',
    label: 'GM申請',
    hint: 'マネージャーが「済」になったとき（GMへの申請）',
  },
  {
    key: 'teamsWebhookUrlReject',
    hasKey: 'hasTeamsWebhookUrlReject',
    label: '差戻',
    hint: 'マネージャーまたはGMが差戻したとき',
  },
];

function emptyTeamsDrafts() {
  return Object.fromEntries(TEAMS_WEBHOOK_CHANNELS.map((c) => [c.key, '']));
}

function emptyTeamsSaved() {
  return Object.fromEntries(TEAMS_WEBHOOK_CHANNELS.map((c) => [c.key, false]));
}

function Settings() {
  const [draftUrls, setDraftUrls] = useState(emptyTeamsDrafts);
  const [hasSavedUrls, setHasSavedUrls] = useState(emptyTeamsSaved);
  const [focusedChannel, setFocusedChannel] = useState(null);
  const [settlementDay, setSettlementDay] = useState('');
  const [settlementTime, setSettlementTime] = useState('');
  const [savedSettlementDay, setSavedSettlementDay] = useState(null);
  const [savedSettlementTime, setSavedSettlementTime] = useState(null);
  const [settlementStart, setSettlementStart] = useState('');
  const [savedSettlementStart, setSavedSettlementStart] = useState(null);
  const [hasSettlementSchedule, setHasSettlementSchedule] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSettlement, setSavingSettlement] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [boxAiCalls, setBoxAiCalls] = useState(null);
  const [aiViewingPrevious, setAiViewingPrevious] = useState(false);

  const applySettings = (data) => {
    setHasSavedUrls(
      Object.fromEntries(
        TEAMS_WEBHOOK_CHANNELS.map((c) => [c.key, Boolean(data[c.hasKey])]),
      ),
    );
    const day =
      data.monthSettlementDay === null || data.monthSettlementDay === undefined
        ? null
        : Number(data.monthSettlementDay);
    const time = data.monthSettlementTime || null;
    setSavedSettlementDay(Number.isFinite(day) ? day : null);
    setSavedSettlementTime(time);
    const start = startMonthFromApi(data.monthSettlementStart);
    setSavedSettlementStart(data.monthSettlementStart || null);
    setHasSettlementSchedule(Boolean(data.hasMonthSettlementSchedule));
    setSettlementDay(Number.isFinite(day) ? String(day) : '');
    setSettlementTime(time || '');
    setSettlementStart(start);
    if (data.boxAiCalls && typeof data.boxAiCalls === 'object') {
      setBoxAiCalls(data.boxAiCalls);
      setAiViewingPrevious(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiCall('/api/settings');
        const data = await readJson(res);
        if (!res.ok || data.status === 'error') {
          throw new Error(data.message || `HTTP ${res.status}`);
        }
        if (!cancelled) {
          setDraftUrls(emptyTeamsDrafts());
          setFocusedChannel(null);
          applySettings(data);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || String(e));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!focusedChannel || saving) {
      return;
    }
    const channel = TEAMS_WEBHOOK_CHANNELS.find((c) => c.key === focusedChannel);
    if (!channel) {
      return;
    }
    const nextUrl = (draftUrls[channel.key] || '').trim();
    const hasSaved = Boolean(hasSavedUrls[channel.key]);
    if (!hasSaved && nextUrl === '') {
      return;
    }
    if (hasSaved && nextUrl === '') {
      const ok = window.confirm(
        `設定済みの Teams Webhook URL（${channel.label}）を削除しますか？`,
      );
      if (!ok) {
        return;
      }
    }
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const res = await apiCall('/api/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [channel.key]: nextUrl }),
      });
      const data = await readJson(res);
      if (!res.ok || data.status === 'error') {
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      setDraftUrls((prev) => ({ ...prev, [channel.key]: '' }));
      applySettings(data);
      setMessage(data.message || `${channel.label}の設定を保存しました`);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettlement = async (e) => {
    e.preventDefault();
    if (savingSettlement) {
      return;
    }
    const dayRaw = settlementDay.trim();
    const timeRaw = settlementTime.trim();
    const startRaw = settlementStart.trim();
    const anySet = dayRaw !== '' || timeRaw !== '' || startRaw !== '';
    const allSet = dayRaw !== '' && timeRaw !== '' && startRaw !== '';
    if (anySet && !allSet) {
      setError('判定開始月・判定基準日・時刻はセットで設定するか、すべて空にしてください');
      return;
    }
    if (dayRaw !== '') {
      const day = Number(dayRaw);
      if (!Number.isInteger(day) || day < 1 || day > 31) {
        setError('判定基準日は1〜31の整数で指定してください');
        return;
      }
    }
    if (startRaw !== '') {
      const month = Number(startRaw);
      if (!Number.isInteger(month) || month < 1 || month > 12) {
        setError('判定開始月を選択してください');
        return;
      }
    }
    setSavingSettlement(true);
    setMessage(null);
    setError(null);
    try {
      const res = await apiCall('/api/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          monthSettlementDay: dayRaw === '' ? null : Number(dayRaw),
          monthSettlementTime: timeRaw === '' ? null : timeRaw,
          monthSettlementStart: startRaw === '' ? null : formatStartYm(startRaw),
        }),
      });
      const data = await readJson(res);
      if (!res.ok || data.status === 'error') {
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      applySettings(data);
      setMessage(data.message || '当月精算の判定設定を保存しました');
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSavingSettlement(false);
    }
  };

  const focusedHasSaved = focusedChannel ? Boolean(hasSavedUrls[focusedChannel]) : false;
  const focusedDraft = focusedChannel ? (draftUrls[focusedChannel] || '').trim() : '';
  const canSave = Boolean(focusedChannel)
    && !saving
    && (focusedHasSaved || focusedDraft !== '');
  const canSaveSettlement = !savingSettlement;
  const aiView = resolveAiMonthView(boxAiCalls, aiViewingPrevious);
  const usagePct = Math.min(100, Math.round((aiView.monthTotal / aiView.monthLimit) * 100));
  const usageOver = aiView.monthTotal >= aiView.monthLimit;

  return (
    <div className="settings-page">
      <h1 className="settings-title">設定</h1>

      {loading ? (
        <p className="settings-status">読み込み中…</p>
      ) : (
        <>
          <form className="settings-form" onSubmit={handleSave}>
            <h2 className="settings-section-title">Teams Webhook URL</h2>
            <p className="settings-help">
              通知チャネルごとに URL を設定します。フォーカスした欄だけ保存されます。
              各チャネルは専用 URL が必須です（未設定の通知は送信しません）。
            </p>
            {TEAMS_WEBHOOK_CHANNELS.map((channel) => {
              const saved = Boolean(hasSavedUrls[channel.key]);
              return (
                <label
                  key={channel.key}
                  className="settings-label"
                  htmlFor={`teams-webhook-${channel.key}`}
                >
                  {channel.label}
                  <span className="settings-channel-hint">{channel.hint}</span>
                  <input
                    id={`teams-webhook-${channel.key}`}
                    className="settings-input"
                    type="text"
                    name={channel.key}
                    value={draftUrls[channel.key] || ''}
                    onChange={(ev) => {
                      const value = ev.target.value;
                      setDraftUrls((prev) => ({ ...prev, [channel.key]: value }));
                    }}
                    onFocus={() => setFocusedChannel(channel.key)}
                    onBlur={() => setFocusedChannel((prev) => (prev === channel.key ? null : prev))}
                    placeholder={saved ? 'URLが設定されています' : ''}
                    autoComplete="off"
                    spellCheck={false}
                    disabled={saving}
                  />
                  {!saved && (
                    <span className="settings-hint-unset">URLが設定されていません</span>
                  )}
                </label>
              );
            })}
            <button
              className={`settings-save-button${canSave ? '' : ' is-disabled'}`}
              type="submit"
              disabled={!canSave}
              aria-disabled={!canSave}
              onMouseDown={(e) => {
                if (canSave) {
                  e.preventDefault();
                }
              }}
            >
              保存する
            </button>
          </form>

          <form className="settings-form settings-settlement-form" onSubmit={handleSaveSettlement}>
            <h2 className="settings-section-title">当月精算の判定</h2>
            <p className="settings-help">
              指定日以降、毎日指定時刻に判定します。当月の電子伝票がすべて担当確認・M確認・GM確認「済」なら、
              当月精算（R1）に「済」を入れます。完了の Teams 通知は通常 P/Q/R 更新直後に送り、
              定時は未通知のまま完了している場合のみ補完します。
              未完了なら、完了していない旨をTeamsへ通知します。
              判定開始月（2026年固定）より前は判定しません。翌月になっても、完了するまで開始月以降の未完了分を毎日送ります。
              翌月の当月分は、指定日になるまで送りません。
            </p>
            {hasSettlementSchedule ? (
              <p className="settings-current-value" role="status">
                現在の設定: {savedSettlementStart ? `${formatStartLabel(savedSettlementStart)}、` : ''}
                {savedSettlementDay}日以降、毎日 {savedSettlementTime}
              </p>
            ) : (
              <p className="settings-hint-unset">判定設定がされていません</p>
            )}
            <div className="settings-row">
              <label className="settings-label" htmlFor="month-settlement-start">
                判定開始月（2026年）
                <select
                  id="month-settlement-start"
                  className="settings-input settings-input-month"
                  value={settlementStart}
                  onChange={(ev) => setSettlementStart(ev.target.value)}
                  disabled={savingSettlement}
                >
                  <option value="">選択</option>
                  {SETTLEMENT_MONTHS.map((month) => (
                    <option key={month} value={String(month)}>
                      {month}月
                    </option>
                  ))}
                </select>
              </label>
              <label className="settings-label" htmlFor="month-settlement-day">
                判定基準（日以降）
                <input
                  id="month-settlement-day"
                  className="settings-input settings-input-narrow"
                  type="number"
                  min={1}
                  max={31}
                  placeholder="例: 20"
                  value={settlementDay}
                  onChange={(ev) => setSettlementDay(ev.target.value)}
                  disabled={savingSettlement}
                />
              </label>
              <label className="settings-label" htmlFor="month-settlement-time">
                毎日の判定時刻
                <input
                  id="month-settlement-time"
                  className="settings-input settings-input-narrow"
                  type="time"
                  value={settlementTime}
                  onChange={(ev) => setSettlementTime(ev.target.value)}
                  disabled={savingSettlement}
                />
              </label>
            </div>
            <button
              className={`settings-save-button${canSaveSettlement ? '' : ' is-disabled'}`}
              type="submit"
              disabled={!canSaveSettlement}
            >
              判定設定を保存
            </button>
          </form>

          <section className="settings-ai-usage" aria-labelledby="box-ai-usage-title">
            <h2 className="settings-section-title" id="box-ai-usage-title">Box AI コール数</h2>
            <div className="settings-ai-month-nav">
              <button
                type="button"
                className="settings-ai-month-btn"
                aria-label="先月"
                disabled={!aiView.canGoPrev}
                onClick={() => setAiViewingPrevious(true)}
              >
                ←
              </button>
              <p className="settings-ai-month-label" aria-live="polite">
                {aiView.monthLabel || '—'}
              </p>
              <button
                type="button"
                className="settings-ai-month-btn"
                aria-label="今月"
                disabled={!aiView.canGoNext}
                onClick={() => setAiViewingPrevious(false)}
              >
                →
              </button>
            </div>
            <p className="settings-ai-usage-summary" role="status">
              {aiView.monthTotal.toLocaleString('ja-JP')} / {aiView.monthLimit.toLocaleString('ja-JP')} 回
              （{usagePct}%）
            </p>
            <div
              className={`settings-ai-bar${usageOver ? ' is-over' : ''}`}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={aiView.monthLimit}
              aria-valuenow={aiView.monthTotal}
              aria-label="Box AI 月間コール数"
            >
              <div
                className="settings-ai-bar-fill"
                style={{ width: `${usagePct}%` }}
              />
            </div>
            {aiView.days.length > 0 ? (
              <ul className="settings-ai-days">
                {[...aiView.days].reverse().map((day) => (
                  <li key={day.date}>
                    <span className="settings-ai-day-date">{formatAiDayLabel(day.date)}</span>
                    <span className="settings-ai-day-count">{Number(day.count) || 0} 回</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="settings-hint-muted">まだ記録がありません</p>
            )}
          </section>
        </>
      )}

      {message && (
        <div className="settings-message settings-message-ok" role="status">
          {message}
        </div>
      )}
      {error && (
        <div className="settings-message settings-message-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

export default Settings;
